import { useEffect, useMemo, useState } from "react";
import { ExternalLink, Info } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, fmtDate } from "@/lib/api";

const C = { refugees: "#b45309", skilled: "#0f766e", other: "#94a3b8", y15_24: "#b45309", y25_64: "#0f766e", y65: "#6366f1", total: "#0f172a" };
const MIG = [["refugees", "Refugees & humanitarian"], ["skilled", "Skilled migrants"], ["other", "Family & other"]];
const AGES = [["y15_24", "Aged 15–24"], ["y25_64", "Aged 25–64"], ["y65", "Aged 65+"], ["total", "All ages (15+)"]];
const k = (v) => (Math.abs(v) >= 1000 ? `${Math.round(v / 1000)}k` : `${v}`);
const num = (v) => (v == null ? "n/a" : Number(v).toLocaleString());
const AX = { tick: { fontSize: 11, fill: "#64748b" } };

const Src = ({ s }) => <a href={s.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[11px] font-medium text-[hsl(var(--facts))] hover:underline">{s.org}<ExternalLink size={10} /></a>;

const Panel = ({ title, note, children, testid, sources = [] }) => (
  <div className="space-y-2" data-testid={testid}>
    <h3 className="text-lg">{title}</h3>
    {note && <p className="text-xs text-slate-500">{note}</p>}
    {children}
    <div className="flex flex-wrap gap-x-4">{sources.map((s) => <Src key={s.url} s={s} />)}</div>
  </div>
);

const MigrationChart = ({ rows }) => (
  <ResponsiveContainer width="100%" height={300}>
    <BarChart data={rows} margin={{ top: 8, right: 8, left: -4, bottom: 0 }}>
      <CartesianGrid stroke="#e2e8f0" vertical={false} />
      <XAxis dataKey="year" {...AX} minTickGap={16} /><YAxis tickFormatter={k} {...AX} />
      <Tooltip formatter={(v, n) => [num(v), MIG.find(([key]) => key === n)?.[1] || n]} />
      <Legend formatter={(n) => MIG.find(([key]) => key === n)?.[1]} wrapperStyle={{ fontSize: 12 }} />
      {MIG.map(([key]) => <Bar key={key} dataKey={key} stackId="m" fill={C[key]} isAnimationActive={false} />)}
    </BarChart>
  </ResponsiveContainer>
);

const NetChart = ({ rows }) => (
  <ResponsiveContainer width="100%" height={220}>
    <BarChart data={rows} margin={{ top: 8, right: 8, left: -4, bottom: 0 }}>
      <CartesianGrid stroke="#e2e8f0" vertical={false} />
      <XAxis dataKey="year" {...AX} minTickGap={16} /><YAxis tickFormatter={k} {...AX} />
      <Tooltip formatter={(v) => [num(v), "Net overseas migration"]} />
      <Bar dataKey="value" isAnimationActive={false}>{rows.map((r) => <Cell key={r.year} fill={r.value < 0 ? "#94a3b8" : "#334155"} />)}</Bar>
    </BarChart>
  </ResponsiveContainer>
);

const UnempChart = ({ rows }) => (
  <ResponsiveContainer width="100%" height={280}>
    <LineChart data={rows} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
      <CartesianGrid stroke="#e2e8f0" vertical={false} />
      <XAxis dataKey="year" {...AX} /><YAxis unit="%" {...AX} />
      <Tooltip formatter={(v, n) => [`${v}%`, AGES.find(([key]) => key === n)?.[1]]} />
      <Legend formatter={(n) => AGES.find(([key]) => key === n)?.[1]} wrapperStyle={{ fontSize: 12 }} />
      {AGES.map(([key]) => <Line key={key} dataKey={key} stroke={C[key]} strokeWidth={key === "total" ? 2 : 3} strokeDasharray={key === "total" ? "5 4" : undefined} dot={false} isAnimationActive={false} connectNulls />)}
    </LineChart>
  </ResponsiveContainer>
);

const CompareBars = ({ rows, keys, mine, unit, stacked }) => {
  const tick = ({ x, y, payload }) => <text x={x} y={y} dy={4} textAnchor="end" fontSize={11} fontWeight={payload.value === mine ? 700 : 400} fill={payload.value === mine ? "#0f172a" : "#64748b"}>{payload.value}</text>;
  return (
    <ResponsiveContainer width="100%" height={rows.length * 22 + 40}>
      <BarChart data={rows} layout="vertical" margin={{ top: 0, right: 16, left: 24, bottom: 0 }}>
        <CartesianGrid stroke="#e2e8f0" horizontal={false} />
        <XAxis type="number" unit={unit} {...AX} /><YAxis type="category" dataKey="name" width={96} tick={tick} interval={0} />
        <Tooltip formatter={(v, n) => [`${v}${unit}`, [...MIG, ...AGES].find(([key]) => key === n)?.[1]]} />
        {stacked && <Legend verticalAlign="top" formatter={(n) => MIG.find(([key]) => key === n)?.[1]} wrapperStyle={{ fontSize: 12 }} />}
        {keys.map((key) => <Bar key={key} dataKey={key} stackId={stacked ? "s" : undefined} fill={C[key]} isAnimationActive={false}>
          {rows.map((r) => <Cell key={r.iso3} fill={C[key]} fillOpacity={r.name === mine ? 1 : 0.45} />)}</Bar>)}
      </BarChart>
    </ResponsiveContainer>
  );
};

const Explainer = () => (
  <aside className="rounded-2xl border border-[hsl(var(--facts)/0.3)] bg-[hsl(var(--facts)/0.05)] p-5 text-sm text-slate-700" data-testid="unemployment-explainer">
    <p className="flex items-center gap-1.5 font-semibold text-[hsl(var(--facts))]"><Info size={15} />How the unemployment rate is calculated</p>
    <p className="mt-2"><b>Unemployment rate = unemployed people ÷ labour force × 100.</b> The labour force is everyone with a job plus everyone who is unemployed.</p>
    <ul className="mt-2 list-disc space-y-1 pl-5">
      <li><b>Unemployed</b> means all three: no paid work in the survey week (not even one hour), actively looked for work in the past four weeks, and able to start now.</li>
      <li><b>Not counted at all:</b> retirees, people who can't work because of illness or disability, full-time carers, students who aren't looking, and anyone who has stopped looking. They are outside the labour force, so they are left out of both sides of the sum.</li>
      <li>Working even one hour a week counts as employed. Children under 15 aren't surveyed, which is why there is no official under-18 rate.</li>
      <li>The 65+ rate is low partly because most people that age are retired and so aren't counted.</li>
    </ul>
    <p className="mt-2 text-xs text-slate-500">Measured by monthly household Labour Force Surveys using International Labour Organization standards, so countries can be compared.</p>
  </aside>
);

const ChipRow = ({ value, set, options, prefix }) => (
  <div className="flex flex-wrap gap-1.5">{options.map(([key, label]) => <button key={key} onClick={() => set(key)} data-testid={`${prefix}-${key}`} className={`chip ${value === key ? "chip-active" : ""}`}>{label}</button>)}</div>
);

export const CountryStats = () => {
  const [d, setD] = useState(null);
  const [age, setAge] = useState("y15_24");
  useEffect(() => {
    let timer;
    const load = () => api.get("/stats/country").then((r) => { if (r.data.loading) timer = setTimeout(load, 8000); setD(r.data); }).catch(() => setD(false));
    load();
    return () => clearTimeout(timer);
  }, []);
  const uneRows = useMemo(() => (d ? [...d.unemployment_compare.rows].filter((r) => r[age] != null).sort((a, b) => b[age] - a[age]) : []), [d, age]);
  if (d === false) return <p className="text-sm text-slate-500" data-testid="stats-error">Official statistics are unavailable right now.</p>;
  if (!d) return <section className="surface h-48 animate-pulse" data-testid="stats-loading" />;
  if (d.loading) return <p className="surface p-5 text-sm text-slate-500" data-testid="stats-preparing">Fetching the latest official statistics. This takes about half a minute…</p>;
  const { country: c, migration: m } = d;
  return (
    <section className="surface space-y-10 p-5 sm:p-6" data-testid="country-stats">
      <div><p className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--facts))]">Official statistics</p>
        <h2 className="mt-1 text-2xl">Immigration and jobs{c.name ? `: ${c.name}` : ""}</h2>
        {!c.covered && <p className="mt-1 text-sm text-slate-600" data-testid="stats-not-covered">Comparable official figures for {c.name || "your country"} aren't published by the OECD, so only the comparison across OECD countries is shown.{!c.code && " Set your location in Settings to see your own country."}</p>}</div>
      {c.covered && <>
        <Panel testid="stats-migration-chart" title={`New permanent residents by type, ${m.series[0]?.year}–${m.series[m.series.length - 1]?.year}`} sources={m.sources}
          note={`${c.iso3 === "AUS" ? "Financial years (e.g. 2025 = 2024–25). Skilled includes their accompanying family. Refugees & humanitarian are Humanitarian Program visa grants. Skilled and family figures by stream start in 1984–85." : "Skilled includes accompanying family of workers. Family & other includes family reunification, free movement within the EU and other categories."}`}>
          <MigrationChart rows={m.series} /></Panel>
        <Panel testid="stats-net-chart" title="Total net migration (shown separately)" sources={[m.net_source]}
          note="Everyone arriving to live for a year or more minus everyone leaving, including students and temporary workers. This is not the same as the permanent residents above.">
          <NetChart rows={m.net} /></Panel>
        <Panel testid="stats-unemployment-chart" title={`Unemployment rate by age, ${d.unemployment.series[0]?.year}–${d.unemployment.series[d.unemployment.series.length - 1]?.year}`} sources={[d.unemployment.source]} note="Annual averages. Official age groups: 15–24, 25–64 and 65+.">
          <UnempChart rows={d.unemployment.series} /></Panel>
      </>}
      <Explainer />
      <Panel testid="stats-migration-compare" title={`Permanent immigration across OECD countries, ${d.migration_compare.year}`} sources={[d.migration_compare.source]} note="New permanent residents per 1,000 people living in each country, by type.">
        <CompareBars rows={d.migration_compare.rows} keys={["refugees", "skilled", "other"]} mine={c.name} unit="" stacked /></Panel>
      <Panel testid="stats-unemployment-compare" title={`Unemployment rate across OECD countries, ${d.unemployment_compare.year}`} sources={[d.unemployment_compare.source]}>
        <ChipRow value={age} set={setAge} options={AGES} prefix="stats-age" />
        <CompareBars rows={uneRows} keys={[age]} mine={c.name} unit="%" /></Panel>
      <p className="text-[11px] text-slate-400" data-testid="stats-updated">Refreshed monthly from official sources, last checked {fmtDate(d.updated_at)}.</p>
    </section>
  );
};
